import { Injectable, inject } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { Observable, catchError, retry, throwError, timer } from 'rxjs'
import { ApiClient } from '@core/http/api-client'
import { LoggerService } from '@core/services/logger.service'
import { environment } from '@env/environment'
import { AlertCategory, AlertSeverity, SuccessionPhase } from '@shared/models/dto'

/**
 * Generic base service for handling async operations with state management.
 *
 * Features:
 * - Automatic retry logic with exponential backoff
 * - Loading state management
 * - Error handling and logging
 * - Type-safe API calls
 * - Observable-based async operations
 *
 * Usage:
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class UserService extends BaseAsyncService {
 *   getUsers(): Observable<User[]> {
 *     return this.get<User[]>('/users');
 *   }
 *
 *   createUser(user: CreateUserRequest): Observable<User> {
 *     return this.post<User>('/users', user);
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class BaseAsyncService {
  protected readonly apiClient = inject(ApiClient)
  protected readonly logger = inject(LoggerService)

  private readonly defaultRetryConfig: RetryConfig = {
    maxRetries: 0,
    delay: 1000,
    backoffMultiplier: 2,
  }

  /**
   * Performs a GET request with automatic retry and error handling.
   * @param endpoint API endpoint
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected get<T>(endpoint: string, config?: AsyncOperationConfig): Observable<T> {
    return this.executeRequest(() => this.apiClient.get<T>(environment.baseUrl + endpoint), config)
  }

  /**
   * Performs a GET request with custom options (params, headers, withCredentials, etc.)
   * and automatic retry and error handling.
   * @param endpoint API endpoint
   * @param options API request options (params, headers, withCredentials, etc.)
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected getWithOptions<T>(
    endpoint: string,
    options?: { params?: Record<string, string>; headers?: Record<string, string>; withCredentials?: boolean },
    config?: AsyncOperationConfig
  ): Observable<T> {
    let httpParams: HttpParams | undefined

    if (options?.params) {
      httpParams = new HttpParams()
      Object.entries(options.params).forEach(([key, value]) => {
        httpParams = httpParams!.set(key, value)
      })
    }

    return this.executeRequest(
      () =>
        this.apiClient.get<T>(environment.baseUrl + endpoint, {
          params: httpParams,
          headers: options?.headers,
          withCredentials: options?.withCredentials,
        }),
      config
    )
  }

  /**
   * Performs a POST request with automatic retry and error handling.
   * @param endpoint API endpoint
   * @param body Request body
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected post<T>(endpoint: string, body: unknown, config?: AsyncOperationConfig): Observable<T> {
    return this.executeRequest(() => this.apiClient.post<T>(environment.baseUrl + endpoint, body), config)
  }

  /**
   * Performs a PUT request with automatic retry and error handling.
   * @param endpoint API endpoint
   * @param body Request body
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected put<T>(endpoint: string, body: unknown, config?: AsyncOperationConfig): Observable<T> {
    return this.executeRequest(() => this.apiClient.put<T>(environment.baseUrl + endpoint, body), config)
  }

  /**
   * Performs a PATCH request with automatic retry and error handling.
   * @param endpoint API endpoint
   * @param body Request body
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected patch<T>(endpoint: string, body: unknown, config?: AsyncOperationConfig): Observable<T> {
    return this.executeRequest(() => this.apiClient.patch<T>(environment.baseUrl + endpoint, body), config)
  }

  /**
   * Performs a DELETE request with automatic retry and error handling.
   * @param endpoint API endpoint
   * @param config Operation configuration
   * @returns Observable of the response
   */
  protected delete<T>(endpoint: string, config?: AsyncOperationConfig): Observable<T> {
    return this.executeRequest(() => this.apiClient.delete<T>(environment.baseUrl + endpoint), config)
  }

  /**
   * Performs a GET request and returns the raw Blob (for file downloads/previews).
   * @param endpoint API endpoint
   * @returns Observable of the Blob
   */
  protected getBlob(endpoint: string): Observable<Blob> {
    return this.apiClient.getBlob(environment.baseUrl + endpoint)
  }

  /**
   * Executes a request with retry logic and error handling.
   * @param requestFn Function that returns the request observable
   * @param config Operation configuration
   * @returns Observable of the response
   */
  private executeRequest<T>(requestFn: () => Observable<T>, config?: AsyncOperationConfig): Observable<T> {
    const retryConfig = config?.retry || this.defaultRetryConfig
    const shouldLogErrors = config?.logErrors !== false

    return requestFn().pipe(
      retry({
        count: retryConfig.maxRetries,
        delay: (error, retryCount) => {
          // Do not retry client errors (4xx) except 429 Too Many Requests,
          // unless the caller explicitly opted in (e.g. SSO callback race condition).
          const status = error?.status as number | undefined
          if (!retryConfig.retryOn4xx && status && status >= 400 && status < 500 && status !== 429) {
            return throwError(() => error)
          }
          const delay = retryConfig.delay * Math.pow(retryConfig.backoffMultiplier || 2, retryCount - 1)
          if (shouldLogErrors) {
            this.logger.warn(
              `Request failed, retrying in ${delay}ms (attempt ${retryCount}/${retryConfig.maxRetries})`,
              error
            )
          }
          return timer(delay)
        },
      }),
      catchError(error => {
        if (shouldLogErrors) {
          this.logger.error('Request failed after all retries', error)
        }
        return throwError(() => error)
      })
    )
  }

  /**
   * Creates a retry configuration with custom settings.
   * @param maxRetries Maximum number of retries
   * @param delay Initial delay in milliseconds
   * @param backoffMultiplier Multiplier for exponential backoff
   * @returns Retry configuration
   */
  protected createRetryConfig(
    maxRetries: number = 3,
    delay: number = 1000,
    backoffMultiplier: number = 2
  ): RetryConfig {
    return { maxRetries, delay, backoffMultiplier }
  }

  /**
   * Creates an operation configuration with custom settings.
   * @param retry Retry configuration
   * @param showLoading Whether to show loading state
   * @param logErrors Whether to log errors
   * @returns Operation configuration
   */
  protected createOperationConfig(
    retry?: RetryConfig,
    showLoading: boolean = true,
    logErrors: boolean = true
  ): AsyncOperationConfig {
    return { retry, showLoading, logErrors }
  }

  buildQuery(params: ListParams): string {
    const searchParams = new URLSearchParams(
      Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)])
    )
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
  }
}

/**
 * Configuration for retry behavior in async operations.
 */
export interface RetryConfig {
  maxRetries: number
  delay: number
  backoffMultiplier?: number
  /** When true, 4xx errors (except 429) are also retried. Use only for endpoints
   *  where a transient 4xx is expected (e.g. SSO callback race condition). */
  retryOn4xx?: boolean
}

/**
 * Configuration for async operations.
 */
export interface AsyncOperationConfig {
  retry?: RetryConfig
  showLoading?: boolean
  logErrors?: boolean
}
export interface ListParams {
  paginated?: boolean
  page?: number
  pageSize?: number
  notaryOfficeId?: number
  phase?: SuccessionPhase
  filter?: string
  old2y?: boolean
  fromEgg?: boolean
  isIdentified?: boolean
  onlyUnread?: boolean
  includeDismissed?: boolean
  successionFileId?: number
  category?: AlertCategory
  severity?: AlertSeverity
}
